import { useContext, useState, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Sparkles, Users, Play, Pause, Music, Compass, Wind } from "lucide-react";
import { EnlightenmentContext } from "@/context/EnlightenmentContext";
import EMDRBall from "@/components/EMDRBall";
import InfoModal from "@/components/InfoModal";
import GarmentsModal from "@/components/GarmentsModal";

const PRIMORDEIA_LORE = `Primordeia is the dream of the union of there to here. À place where happiness abounds and nature is not something out there to observe but something deeply integrated within the human psyche that harmonizes with all of creation. To get to Primordeia the path is sometimes fettered with distractions, untruths and insecurities: blockages to what is really true, not the truth in itself.

Our founders dreamed separately of Primordeia until fate brought them together to proceed with building a bridge to make there to here accessible for everyone.

Welcome to Primordeia.

Where the Holy Spirit in Nature awaits.`;

const SFRH_LORE = `SFRH means a single book or united chakra from the Tree of Life uniting into the Merkaba. The Book of Seven Seals is the SFRH. There is no I in SFRH. The new dispensation is We Are That We Are. And We Are.`;

const Home = () => {
  const navigate = useNavigate();
  const { carrots, blueberries, getCurrentTierData } = useContext(EnlightenmentContext);
  const currentTierData = getCurrentTierData();
  const [isPlaying, setIsPlaying] = useState(false);
  const [showEMDR, setShowEMDR] = useState(false);
  const [showGarments, setShowGarments] = useState(false);
  const audioRef = useRef(null);
  const [journeyStarted, setJourneyStarted] = useState(() => {
    return localStorage.getItem('journeyStarted') === 'true';
  });

  const toggleMusic = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio('https://customer-assets.emergentagent.com/job_scent-select-bunny/artifacts/nh49eu3p_Willow_Over_Water.mp3');
      audioRef.current.loop = true;
      audioRef.current.volume = 0.4;
      audioRef.current.onended = () => setIsPlaying(false);
    }
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 relative">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-lg mx-auto text-center relative z-10"
        data-testid="home-page"
      >
        {/* Papaya Image */}
        <motion.img
          src="https://customer-assets.emergentagent.com/job_scent-select-bunny/artifacts/pmy62wcq_1000014737.png"
          alt="Papaya, the White Rabbit"
          className="w-full max-w-xs mx-auto mb-10 rounded-3xl bunny-float"
          style={{ boxShadow: '0 24px 64px rgba(79, 55, 139, 0.18)' }}
          data-testid="papaya-image"
        />

        <motion.h1
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="relative mb-4 flex flex-col items-center"
          data-testid="app-title"
        >
          {/* Tropical decorations */}
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-full flex justify-center items-center pointer-events-none select-none"
            style={{ fontSize: '2rem', letterSpacing: '0.3em' }}>
            <span className="absolute -left-4 sm:-left-8 -top-4 text-3xl sm:text-4xl" style={{ transform: 'rotate(-30deg)' }}>&#x1F334;</span>
            <span className="absolute -right-4 sm:-right-8 -top-4 text-3xl sm:text-4xl" style={{ transform: 'rotate(30deg) scaleX(-1)' }}>&#x1F334;</span>
            <span className="absolute -left-1 sm:-left-4 top-10 text-xl sm:text-2xl" style={{ transform: 'rotate(-15deg)' }}>&#x1F34C;</span>
            <span className="absolute -right-1 sm:-right-4 top-10 text-xl sm:text-2xl" style={{ transform: 'rotate(15deg)' }}>&#x1F34C;</span>
            <span className="absolute left-6 sm:left-2 -top-1 text-lg sm:text-xl" style={{ transform: 'rotate(-50deg)' }}>&#x1F343;</span>
            <span className="absolute right-6 sm:right-2 -top-1 text-lg sm:text-xl" style={{ transform: 'rotate(50deg) scaleX(-1)' }}>&#x1F343;</span>
          </div>
          <span
            className="text-5xl sm:text-6xl lg:text-7xl tracking-widest font-black relative z-10 py-3"
            style={{
              fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
              fontWeight: 900,
              background: 'linear-gradient(180deg, #5C3D1A 0%, #8B6914 25%, #D4A537 50%, #8B6914 75%, #5C3D1A 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textShadow: 'none',
              letterSpacing: '0.12em',
              filter: 'drop-shadow(0 2px 4px rgba(92, 61, 26, 0.15))',
            }}
          >
            PRIMORDEIA
          </span>
          {/* Floating Who-is-Primordeia question mark — sits at the top-right of the title */}
          <div className="absolute -top-1 -right-2 sm:-right-4 z-20">
            <InfoModal
              title="Who is Primordeia?"
              testId="primordeia-info"
            >
              {PRIMORDEIA_LORE}
            </InfoModal>
          </div>
          {/* Subtle underline flourish */}
          <div className="w-48 sm:w-64 h-0.5 mt-1 rounded-full"
            style={{ background: 'linear-gradient(90deg, transparent, #8B6914, #D4A537, #8B6914, transparent)' }} />
        </motion.h1>

        {/* 3D GARMENTS — opens Andie Vargas' atelier; gentle gold shimmer once on page load */}
        <motion.button
          type="button"
          onClick={() => setShowGarments(true)}
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          whileHover={{ scale: 1.04, letterSpacing: '0.42em' }}
          whileTap={{ scale: 0.97 }}
          className="relative overflow-hidden mb-3 px-4 py-1.5 rounded-full text-[11px] sm:text-xs font-bold"
          style={{
            fontFamily: "'Cinzel', serif",
            letterSpacing: '0.35em',
            color: '#8B6914',
            background: 'linear-gradient(135deg, rgba(212,165,116,0.12), rgba(139,105,20,0.10))',
            border: '1px solid rgba(139,105,20,0.35)',
            boxShadow: '0 2px 10px rgba(139,105,20,0.10)',
          }}
          data-testid="home-garments-link"
          aria-label="Open the 3D garments atelier"
        >
          <span className="relative z-10">3D GARMENTS</span>
          {/* Silk-thread catching light — one-shot diagonal shimmer 0.6s after the pill fades in */}
          <motion.span
            aria-hidden="true"
            initial={{ x: '-120%' }}
            animate={{ x: '220%' }}
            transition={{ delay: 0.9, duration: 1.4, ease: 'easeInOut' }}
            className="pointer-events-none absolute top-0 left-0 h-full w-1/2"
            style={{
              background: 'linear-gradient(115deg, transparent 0%, transparent 30%, rgba(255,240,200,0.85) 50%, transparent 70%, transparent 100%)',
              filter: 'blur(1px)',
            }}
          />
          {/* Two tiny gold "thread-end" sparkles that twinkle once with the shimmer */}
          <motion.span
            aria-hidden="true"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: [0, 1, 0], scale: [0, 1.2, 0] }}
            transition={{ delay: 1.4, duration: 0.6, ease: 'easeOut' }}
            className="pointer-events-none absolute top-1 left-3 w-1 h-1 rounded-full"
            style={{ background: '#FFE08A', boxShadow: '0 0 6px 1px #FFD56B' }}
          />
          <motion.span
            aria-hidden="true"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: [0, 1, 0], scale: [0, 1.2, 0] }}
            transition={{ delay: 1.7, duration: 0.6, ease: 'easeOut' }}
            className="pointer-events-none absolute bottom-1 right-4 w-1 h-1 rounded-full"
            style={{ background: '#FFE08A', boxShadow: '0 0 6px 1px #FFD56B' }}
          />
        </motion.button>

        {/* Willow Over Water player */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex items-center justify-center gap-3 mb-4"
        >
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={toggleMusic}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{
              background: isPlaying
                ? 'linear-gradient(135deg, #D4A574 0%, #C4956A 100%)'
                : 'rgba(79, 55, 139, 0.1)',
              boxShadow: isPlaying ? '0 4px 16px rgba(212, 165, 116, 0.3)' : 'none'
            }}
            data-testid="home-music-toggle"
          >
            {isPlaying
              ? <Pause size={16} style={{ color: '#FFFFFF' }} />
              : <Play size={16} style={{ color: '#4F378B', marginLeft: 1 }} />}
          </motion.button>
          <div className="flex items-center gap-1.5">
            <Music size={14} style={{ color: '#8B7DA8' }} />
            <span className="text-xs" style={{ color: '#8B7DA8', fontFamily: "'Nunito', sans-serif", fontStyle: 'italic' }}>
              Willow Over Water — SFRH
            </span>
            <InfoModal title="SFRH" testId="sfrh-info" className="ml-1">
              {SFRH_LORE}
            </InfoModal>
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-lg sm:text-xl mb-3"
          style={{ fontFamily: "'Nunito', sans-serif", color: '#3D3352', fontWeight: 600 }}
          data-testid="subtitle"
        >
          Follow the White Rabbit
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="text-sm mb-10 max-w-md mx-auto leading-relaxed"
          style={{ fontFamily: "'Nunito', sans-serif", color: '#8B7DA8' }}
        >
          Follow the white rabbit back into Primordeia — the garden of paradise. Stop living under the stick and follow holy blueberries to sacred carrots.
        </motion.p>

        {/* Current Status Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.9 }}
          className="rounded-2xl p-6 mb-8"
          style={{
            backgroundColor: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 12px 40px rgba(79, 55, 139, 0.1)'
          }}
          data-testid="journey-status"
        >
          <h2
            className="text-base font-bold mb-4"
            style={{ fontFamily: "'Fredoka', sans-serif", color: '#4F378B' }}
          >
            Your Journey
          </h2>

          {/* Tier Name */}
          <div
            className="text-center p-4 rounded-xl mb-4"
            style={{ backgroundColor: '#F0E8F8' }}
          >
            <div className="text-xs font-semibold mb-1" style={{ color: '#8B7DA8' }}>
              Current Tier
            </div>
            <div className="text-2xl font-bold" style={{ color: '#4F378B', fontFamily: "'Fredoka', sans-serif" }}>
              {currentTierData.name}
            </div>
            <div className="text-xs mt-1" style={{ color: '#8B7DA8' }}>
              {currentTierData.description}
            </div>
          </div>

          {/* Progress bar */}
          <div className="mb-4">
            <div className="flex justify-between text-xs mb-1">
              <span style={{ color: '#8B7DA8' }}>Blueberries this tier</span>
              <span style={{ color: '#4F378B', fontWeight: 700 }}>{blueberries}/5</span>
            </div>
            <div className="w-full h-2 rounded-full" style={{ backgroundColor: '#E8E0F0' }}>
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${(blueberries / 5) * 100}%`,
                  background: 'linear-gradient(90deg, #4F378B, #D4A574)'
                }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold" style={{ color: '#7B5EA7', fontFamily: "'Fredoka', sans-serif" }}>
                {blueberries}
              </div>
              <div className="text-xs" style={{ color: '#8B7DA8' }}>Blueberries</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold" style={{ color: '#D4A574', fontFamily: "'Fredoka', sans-serif" }}>
                {carrots}
              </div>
              <div className="text-xs" style={{ color: '#8B7DA8' }}>Carrots</div>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <div className="relative inline-block">
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1 }}
            whileHover={{ scale: 1.03, y: -2 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => { localStorage.setItem('journeyStarted', 'true'); setJourneyStarted(true); navigate('/journey'); }}
            className="px-10 py-4 rounded-full text-base font-bold"
            style={{
              background: 'linear-gradient(135deg, #4F378B 0%, #7B5EA7 100%)',
              color: '#FFFFFF',
              fontFamily: "'Nunito', sans-serif",
              boxShadow: '0 16px 40px rgba(79, 55, 139, 0.3)'
            }}
            data-testid="begin-journey-button"
          >
            <span className="flex items-center gap-2">
              <Sparkles size={20} strokeWidth={2.5} />
              {journeyStarted ? 'Continue the Journey' : 'Begin the Journey'}
            </span>
          </motion.button>

          {/* "New" pulse badge — draws attention to the road-ahead feature */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 1.4, type: 'spring', stiffness: 300 }}
            className="absolute -top-2 -right-2 pointer-events-none"
            data-testid="journey-new-badge"
          >
            <motion.div
              animate={{ scale: [1, 1.12, 1], boxShadow: [
                '0 0 0 0 rgba(212, 165, 116, 0.7)',
                '0 0 0 10px rgba(212, 165, 116, 0)',
                '0 0 0 0 rgba(212, 165, 116, 0)'
              ] }}
              transition={{ repeat: Infinity, duration: 2, ease: 'easeOut' }}
              className="px-2.5 py-1 rounded-full text-[10px] font-bold flex items-center gap-1"
              style={{
                background: 'linear-gradient(135deg, #D4A574, #E5C28F)',
                color: '#3D2A0E',
                fontFamily: "'Fredoka', sans-serif",
                letterSpacing: '0.08em',
                border: '1.5px solid #FFF',
              }}
            >
              <Sparkles size={10} strokeWidth={2.5} /> NEW
            </motion.div>
          </motion.div>
        </div>

        {/* Preview the Road Ahead — quick shortcut */}
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate('/journey?preview=road-ahead')}
          className="mt-3 px-6 py-2.5 rounded-full text-xs font-semibold flex items-center gap-2 mx-auto"
          style={{
            background: 'rgba(255,255,255,0.85)',
            color: '#4F378B',
            fontFamily: "'Nunito', sans-serif",
            border: '1px dashed rgba(79,55,139,0.35)',
            letterSpacing: '0.04em'
          }}
          data-testid="preview-road-ahead-home-btn"
        >
          <Compass size={14} />
          Preview the Road Ahead
        </motion.button>

        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate('/community')}
          className="mt-3 px-8 py-3 rounded-full text-sm font-semibold flex items-center gap-2 mx-auto"
          style={{
            backgroundColor: 'rgba(255,255,255,0.85)',
            color: '#4F378B',
            fontFamily: "'Nunito', sans-serif",
            boxShadow: '0 4px 16px rgba(79, 55, 139, 0.1)',
            border: '1px solid #E8E0F0'
          }}
          data-testid="community-wisdom-button"
        >
          <Users size={16} />
          Community Wisdom
        </motion.button>

        {/* Trauma Chill Zone — instant access to the EMDR ball for grounding */}
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setShowEMDR(true)}
          className="mt-3 px-8 py-3 rounded-full text-sm font-semibold flex items-center gap-2 mx-auto"
          style={{
            background: 'linear-gradient(135deg, #1a2840 0%, #2D1F50 100%)',
            color: '#E8E0F8',
            fontFamily: "'Nunito', sans-serif",
            boxShadow: '0 6px 20px rgba(45, 31, 80, 0.35)',
            border: '1px solid rgba(123,94,167,0.4)'
          }}
          data-testid="trauma-chill-zone-button"
        >
          <Wind size={16} />
          Trauma Chill Zone
        </motion.button>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="mt-4 text-xs"
          style={{ color: '#8B7DA8', fontFamily: "'Nunito', sans-serif" }}
        >
          Click the bunny in the corner to speak with Papaya
        </motion.p>
      </motion.div>
      <EMDRBall open={showEMDR} onClose={() => setShowEMDR(false)} />
      <GarmentsModal open={showGarments} onClose={() => setShowGarments(false)} />
    </div>
  );
};

export default Home;
